/**
 * i18next Configuration
 *
 * WHAT: Initializes i18next with browser language detection and locale bundles.
 * WHY:  Enables full internationalization with user preference persistence and
 *       RTL support for Arabic.
 * HOW:  Loads bundled locale JSON files (no external CDN). Language detection
 *       priority: user preference (from AuthContext) → browser → fallback 'en'.
 *
 * Locale files: apps/web/src/i18n/locales/{en,ar}.json
 * CMS overrides: useTranslationWithCMS hook fetches tenant-specific overrides.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import en from './locales/en.json';
import ar from './locales/ar.json';

/** Supported locale codes — must match LOCALE_OPTIONS in @amuster/types */
export const SUPPORTED_LOCALES = ['en', 'ar'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

/** RTL locales */
export const RTL_LOCALES: ReadonlySet<string> = new Set(['ar']);

/**
 * Check if a locale code is RTL.
 */
export function isRtlLocale(locale: string): boolean {
  return RTL_LOCALES.has(locale);
}

/**
 * Apply document-level direction attribute for the given locale.
 * Updates <html dir="..."> and <html lang="...">.
 */
export function applyLocaleDirection(locale: string): void {
  const dir = isRtlLocale(locale) ? 'rtl' : 'ltr';
  document.documentElement.setAttribute('dir', dir);
  document.documentElement.setAttribute('lang', locale);
}

// ── i18next init ──────────────────────────────────────────────────────────

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      ar: { translation: ar },
    },
    fallbackLng: 'en',
    supportedLngs: [...SUPPORTED_LOCALES],

    // Detection priority: localStorage → navigator → fallback
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'amuster_locale',
      caches: ['localStorage'],
    },

    interpolation: {
      escapeValue: false, // React handles XSS protection
    },

    // Namespace defaults
    defaultNS: 'translation',
    ns: ['translation'],

    // Avoid suspense flicker — resources are bundled inline
    react: {
      useSuspense: false,
    },
  });

// Apply initial direction
applyLocaleDirection(i18n.language);

// Update direction on language change
i18n.on('languageChanged', (lng: string) => {
  applyLocaleDirection(lng);
  localStorage.setItem('amuster_locale', lng);
});

export default i18n;
